import React from 'react';
import { MyloLabLaunchPage } from '~/features/mylo-lab/MyloLabLaunchPage';
import { I18nextProvider } from 'react-i18next';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { i18n } from '~/app/i18n';
import {
  ActiveWorkspaceProvider,
  AuthProvider,
  useAuth,
  useWorkspace,
} from '~/app/providers';
import { ThemeProvider } from '~/app/theme';
import { RegionProvider, useRegion } from '~/app/region';
import { AppShell } from '~/components/layout/AppShell';
import { AuthCallbackPage, LoginPage } from '~/features/auth/AuthPages';
import {
  RegionConnectionPage,
  RegionSelectionPage,
} from '~/features/auth/RegionPages';
import { OnboardingPage } from '~/features/company/OnboardingPage';
import { WorkspaceSettingsPage } from '~/features/company/WorkspaceSettingsPage';
import { WorkspaceMembersPage } from '~/features/company/WorkspaceMembersPage';
import { AccountProfilePage } from '~/features/account/AccountProfilePage';
import { BillingPage } from '~/features/billing/BillingPage';
import { DeveloperApiPage } from '~/features/developer-api/DeveloperApiPage';
import { ApiDocumentationPage } from '~/features/developer-api/ApiDocumentationPage';
import {
  readBillingDialog,
  setBillingDialog,
} from '~/features/billing/billingDialogState';
import { InviteAcceptPage } from '~/features/company/WorkspaceManagement';
import { WorkspaceSwitcherModal } from '~/features/company/WorkspaceSwitcherModal';
import { AgentLaunchDraftProvider } from '~/features/studio/agentLaunchDraft';
import { HomePage } from '~/features/studio/HomePage';

const AgentPage = React.lazy(() =>
  import('~/features/studio/AgentPage').then(module => ({
    default: module.AgentPage,
  }))
);

const AssetsPage = React.lazy(() =>
  import('~/features/assets/AssetsPage').then(module => ({
    default: module.AssetsPage,
  }))
);

const VideoEditorStartPage = React.lazy(() =>
  import('~/features/video-editor/VideoEditorStartPage').then(module => ({
    default: module.VideoEditorStartPage,
  }))
);

const VideoEditorPage = React.lazy(() =>
  import('~/features/video-editor/StandaloneVideoEditorPage').then(module => ({
    default: module.StandaloneVideoEditorPage,
  }))
);

const LazyRoute = ({
  children,
}: React.PropsWithChildren): React.ReactElement => (
  <React.Suspense fallback={<div className="min-h-screen bg-surface" />}>
    {children}
  </React.Suspense>
);

class ErrorBoundary extends React.Component<
  React.PropsWithChildren,
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  render(): React.ReactNode {
    return this.state.failed ? (
      <main className="grid min-h-screen place-items-center bg-surface p-6 text-center text-foreground">
        <div>
          <h1 className="text-2xl font-semibold">
            {i18n.t('common:errorStart')}
          </h1>
          <button
            className="mt-5 rounded-xl bg-primary px-4 py-3 font-semibold text-white"
            onClick={() => window.location.reload()}
          >
            {i18n.t('common:reload')}
          </button>
        </div>
      </main>
    ) : (
      this.props.children
    );
  }
}

const Protected = ({
  requireCompany = false,
  children,
}: React.PropsWithChildren<{ requireCompany?: boolean }>): React.ReactNode => {
  const { session, ready } = useAuth();
  const { activeWorkspace, companies } = useWorkspace();
  const location = useLocation();
  if (!ready) return <div className="min-h-screen bg-surface" />;
  if (!session)
    return (
      <Navigate
        replace
        to={`/login?returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`}
      />
    );
  if (requireCompany && companies.length === 0)
    return (
      <Navigate
        replace
        to={`/onboarding?returnTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`}
      />
    );
  if (requireCompany && companies.length > 1 && !activeWorkspace)
    return (
      <WorkspaceSwitcherModal dismissible={false} onClose={() => undefined} />
    );
  return children;
};

const LegacyBillingRedirect = (): React.ReactElement => {
  const location = useLocation();
  return (
    <Navigate
      replace
      to={{
        pathname: '/workspaces/billing',
        search: location.search,
      }}
    />
  );
};

const BillingRoute = (): React.ReactElement => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasInvalidDialog =
    params.has('dialog') && readBillingDialog(location.search) === null;

  if (hasInvalidDialog) {
    return (
      <Navigate
        replace
        to={{
          pathname: location.pathname,
          search: setBillingDialog(location.search, null),
        }}
      />
    );
  }

  return <BillingPage />;
};

export const Router = (): React.ReactElement => (
  <BrowserRouter>
    <Routes>
      <Route path="/" element={<HomePage />} />

      <Route element={<RegionalSessionBoundary />}>
        <Route path="login" element={<LoginPage />} />
        <Route path="auth/callback/:provider" element={<AuthCallbackPage />} />
        <Route
          path="onboarding"
          element={
            <Protected>
              <OnboardingPage />
            </Protected>
          }
        />
        <Route
          path="join/:region/:token"
          element={
            <Protected>
              <InviteAcceptPage />
            </Protected>
          }
        />
        <Route
          path="auth/region-callback/:region"
          element={<RegionalAuthHandoff />}
        />
        <Route
          element={
            <Protected requireCompany>
              <Outlet />
            </Protected>
          }
        >
          <Route
            path="workspaces/developer-api/docs"
            element={<ApiDocumentationPage />}
          />
        </Route>

        <Route
          element={
            <Protected requireCompany>
              <AgentLaunchDraftProvider>
                <AppShell />
              </AgentLaunchDraftProvider>
            </Protected>
          }
        >
          <Route path="lab/launch" element={<MyloLabLaunchPage />} />
          <Route path="apps" element={<Navigate replace to="/" />} />
          <Route path="custom" element={<Navigate replace to="/" />} />
          <Route
            path="agent/:sessionId?"
            element={
              <LazyRoute>
                <AgentPage />
              </LazyRoute>
            }
          />
          <Route path="workflows" element={<Navigate replace to="/" />} />
          <Route path="characters" element={<Navigate replace to="/" />} />
          <Route path="watch" element={<Navigate replace to="/" />} />
          <Route
            path="assets"
            element={
              <LazyRoute>
                <AssetsPage />
              </LazyRoute>
            }
          />
          <Route
            path="video-editor"
            element={
              <LazyRoute>
                <VideoEditorStartPage />
              </LazyRoute>
            }
          />
          <Route
            path="video-editor/:projectId"
            element={
              <LazyRoute>
                <VideoEditorPage />
              </LazyRoute>
            }
          />
          <Route path="account/profile" element={<AccountProfilePage />} />
          <Route path="billing" element={<LegacyBillingRedirect />} />
          <Route path="workspaces" element={<WorkspaceSettingsPage />} />
          <Route
            path="workspaces/settings"
            element={<Navigate replace to="/workspaces" />}
          />
          <Route path="workspaces/members" element={<WorkspaceMembersPage />} />
          <Route path="workspaces/billing" element={<BillingRoute />} />
          <Route
            path="workspaces/developer-api"
            element={<DeveloperApiPage />}
          />
        </Route>
      </Route>

      <Route path="/app" element={<Navigate replace to="/" />} />
      <Route path="/app/*" element={<LegacyAppRedirect />} />
      <Route path="*" element={<Navigate replace to="/" />} />
    </Routes>
  </BrowserRouter>
);

const RegionalSessionBoundary = (): React.ReactElement => {
  const { state } = useRegion();
  if (state === 'unselected') return <RegionSelectionPage />;
  if (state !== 'ready') return <RegionConnectionPage />;
  return (
    <AuthProvider>
      <ActiveWorkspaceProvider>
        <Outlet />
      </ActiveWorkspaceProvider>
    </AuthProvider>
  );
};

const LegacyAppRedirect = (): React.ReactElement => {
  const location = useLocation();
  const pathname = location.pathname.replace(/^\/app/, '') || '/';
  return (
    <Navigate
      replace
      to={{
        pathname,
        search: location.search,
        hash: location.hash,
      }}
    />
  );
};

const RegionalAuthHandoff = (): React.ReactElement => {
  const { refresh } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get('returnTo') || '/';
  React.useEffect(() => {
    void refresh().then(() => {
      window.location.replace(returnTo.startsWith('/') ? returnTo : '/');
    });
  }, [refresh, returnTo]);
  return <div className="min-h-screen bg-surface" />;
};

export const App = (): React.ReactElement => (
  <ErrorBoundary>
    <ThemeProvider>
      <I18nextProvider i18n={i18n}>
        <RegionProvider>
          <Router />
        </RegionProvider>
      </I18nextProvider>
    </ThemeProvider>
  </ErrorBoundary>
);
